/** Drawing annotation data model — follows ASME Y14.2 / Y14.5 conventions */

export type Vec2 = [number, number];

// ── Annotation union ──────────────────────────────────────

export type Annotation =
  | LinearDimension
  | RadialDimension
  | DiameterDimension
  | AngularDimension
  | OrdinateDimension
  | CenterlineMark
  | LeaderNote
  | Balloon
  | GdtFrame
  | SurfaceFinish
  | TextNote;

export type AnnotationType = Annotation["kind"];

// ── Base ──────────────────────────────────────────────────

interface AnnotationBase {
  id: string;
  kind: string;
  /** Owning view quadrant — annotations are relative to a view */
  viewId: string;
}

// ── Dimensions ────────────────────────────────────────────

export interface LinearDimension extends AnnotationBase {
  kind: "linear";
  p1: Vec2;
  p2: Vec2;
  /** Offset distance of dimension line from the measured points */
  offset: number;
  /** Orientation: horizontal, vertical, or aligned */
  orientation: "horizontal" | "vertical" | "aligned";
  /** Override text — if empty, computed from geometry */
  textOverride?: string;
  /** Tolerance display */
  tolerance?: { type: "symmetric" | "deviation" | "limits"; upper: number; lower: number };
}

export interface RadialDimension extends AnnotationBase {
  kind: "radial";
  center: Vec2;
  radius: number;
  /** Angle in degrees where the leader points */
  leaderAngle: number;
  textOverride?: string;
}

export interface DiameterDimension extends AnnotationBase {
  kind: "diameter";
  center: Vec2;
  diameter: number;
  /** Angle in degrees for leader orientation */
  leaderAngle: number;
  textOverride?: string;
}

export interface AngularDimension extends AnnotationBase {
  kind: "angular";
  vertex: Vec2;
  p1: Vec2;
  p2: Vec2;
  /** Arc radius for the dimension arc */
  arcRadius: number;
  textOverride?: string;
}

export interface OrdinateDimension extends AnnotationBase {
  kind: "ordinate";
  origin: Vec2;
  point: Vec2;
  /** "x" measures horizontal distance, "y" measures vertical */
  axis: "x" | "y";
  /** Jog position for the leader line */
  jogOffset: number;
}

// ── Centerlines ───────────────────────────────────────────

export interface CenterlineMark extends AnnotationBase {
  kind: "centerline";
  /** "cross" = center mark at a point; "line" = centerline between two points */
  style: "cross" | "line";
  p1: Vec2;
  p2?: Vec2;
  /** Arm length for cross marks */
  armLength: number;
}

// ── Notes & Leaders ───────────────────────────────────────

export interface LeaderNote extends AnnotationBase {
  kind: "leader";
  /** Points defining the leader path (last = arrow tip) */
  points: Vec2[];
  text: string;
  fontSize: number;
}

export interface Balloon extends AnnotationBase {
  kind: "balloon";
  center: Vec2;
  /** Leader endpoint (arrow tip on the part) */
  leaderTip: Vec2;
  /** Item number or text */
  itemNumber: string;
  radius: number;
}

export interface TextNote extends AnnotationBase {
  kind: "text";
  position: Vec2;
  text: string;
  fontSize: number;
  fontWeight: "normal" | "bold";
  rotation: number;
}

// ── GD&T / Surface Finish ─────────────────────────────────

export type GdtCharacteristic =
  | "straightness" | "flatness" | "circularity" | "cylindricity"
  | "perpendicularity" | "angularity" | "parallelism"
  | "position" | "concentricity" | "symmetry"
  | "profileLine" | "profileSurface"
  | "circularRunout" | "totalRunout";

export interface GdtFrame extends AnnotationBase {
  kind: "gdt";
  position: Vec2;
  /** Leader endpoint (arrow on geometry) */
  leaderTip: Vec2;
  characteristic: GdtCharacteristic;
  toleranceValue: string;
  /** Material condition modifier */
  modifier?: "M" | "L" | "S";
  datumA?: string;
  datumB?: string;
  datumC?: string;
}

export interface SurfaceFinish extends AnnotationBase {
  kind: "surfaceFinish";
  position: Vec2;
  leaderTip: Vec2;
  /** Ra value in micrometers */
  roughness: string;
  /** Process note */
  process?: string;
  /** Lay direction symbol */
  lay?: "=" | "⊥" | "X" | "M" | "C" | "R";
}

// ── Helpers ───────────────────────────────────────────────

let _counter = 0;
export function annotationId(): string {
  return `ann_${Date.now().toString(36)}_${(_counter++).toString(36)}`;
}

/** Distance between two 2D points */
export function dist2d(a: Vec2, b: Vec2): number {
  return Math.sqrt((b[0] - a[0]) ** 2 + (b[1] - a[1]) ** 2);
}
